import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ReplayIcon from "@mui/icons-material/Replay";
import { Link as RouterLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "../../../lib/supabase";
import { useThemeMode } from "../../../theme/ThemeModeProvider";
import { chatRailTokens } from "../tokens";
import { useChatRail } from "../ChatRail/ChatRailProvider";

interface RunRow {
  id: string;
  agent_id: string;
  conversation_id: string | null;
  status: string;
  cost_usd: number | string;
  input_tokens: number;
  output_tokens: number;
  iterations: number;
  langfuse_trace_id: string | null;
  error: unknown;
  cancel_requested_at: string | null;
  enqueued_at: string | null;
  started_at: string | null;
  completed_at: string | null;
}

interface ToolCallRow {
  id: string;
  iteration: number;
  kind: string;
  skill_name: string;
  input: unknown;
  output: unknown;
  status: string;
  duration_ms: number | null;
  error: string | null;
  created_at: string | null;
}

const MIN_W = 380;
const MAX_W = 1200;
const LS_KEY = "mannaRunDrawerWidth";

function readWidth(): number {
  if (typeof window === "undefined") return 480;
  const raw = window.localStorage.getItem(LS_KEY);
  const n = raw ? Number.parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n >= MIN_W && n <= MAX_W ? n : 480;
}

function clamp(n: number) {
  return Math.max(MIN_W, Math.min(MAX_W, n));
}

function fmtDuration(ms: number | null): string {
  if (ms == null) return "—";
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(2)}s`;
}

export function RunDetailDrawer({ runId, onClose }: { runId: string; onClose: () => void }) {
  const [replaying, setReplaying] = useState(false);
  const [replaySnack, setReplaySnack] = useState<string | null>(null);

  async function handleReplay() {
    setReplaying(true);
    try {
      const { data, error } = await supabase.rpc("replay_run", { p_run_id: runId });
      if (error) throw error;
      setReplaySnack(`Replay queued — run ${String(data).slice(0, 8)}…`);
    } catch (e) {
      setReplaySnack(`Replay failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setReplaying(false);
    }
  }
  const { mode } = useThemeMode();
  const T = chatRailTokens(mode);
  const { widthPx: railW } = useChatRail();
  const [drawerW, setDrawerWState] = useState<number>(readWidth);
  const dragRef = useRef<{ startX: number; startW: number } | null>(null);

  const setDrawerW = useCallback((next: number) => {
    const w = clamp(Math.round(next));
    setDrawerWState(w);
    window.localStorage.setItem(LS_KEY, String(w));
  }, []);

  const handlersRef = useRef({
    move: (_e: PointerEvent) => {},
    up: (_e: PointerEvent) => {},
  });
  const stableMoveRef = useRef((e: PointerEvent) => handlersRef.current.move(e));
  const stableUpRef = useRef((e: PointerEvent) => handlersRef.current.up(e));

  useEffect(() => {
    handlersRef.current.move = (e: PointerEvent) => {
      if (!dragRef.current) return;
      const dx = dragRef.current.startX - e.clientX;
      setDrawerW(dragRef.current.startW + dx);
    };
    handlersRef.current.up = () => {
      dragRef.current = null;
      window.removeEventListener("pointermove", stableMoveRef.current);
      window.removeEventListener("pointerup", stableUpRef.current);
    };
  }, [setDrawerW]);

  const onPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      dragRef.current = { startX: e.clientX, startW: drawerW };
      window.addEventListener("pointermove", stableMoveRef.current);
      window.addEventListener("pointerup", stableUpRef.current);
    },
    [drawerW],
  );

  useEffect(
    () => () => {
      window.removeEventListener("pointermove", stableMoveRef.current);
      window.removeEventListener("pointerup", stableUpRef.current);
    },
    [],
  );

  const { data: run, isLoading } = useQuery({
    queryKey: ["agent-run", runId],
    queryFn: async (): Promise<RunRow> => {
      const { data, error } = await supabase
        .from("agent_runs_view")
        .select("*")
        .eq("id", runId)
        .single();
      if (error) throw error;
      return data as RunRow;
    },
    refetchInterval: 4000,
  });

  const { data: toolCalls = [] } = useQuery({
    queryKey: ["run-tool-calls", runId],
    queryFn: async (): Promise<ToolCallRow[]> => {
      const { data, error } = await supabase
        .from("tool_calls_view")
        .select("*")
        .eq("run_id", runId)
        .order("iteration", { ascending: true })
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as ToolCallRow[];
    },
    refetchInterval: 4000,
  });

  const errObj = (run?.error ?? null) as { code?: string; message?: string } | null;
  const grouped = new Map<number, ToolCallRow[]>();
  for (const tc of toolCalls) {
    const arr = grouped.get(tc.iteration) ?? [];
    arr.push(tc);
    grouped.set(tc.iteration, arr);
  }
  const iterationKeys = Array.from(grouped.keys()).sort((a, b) => a - b);

  return (
    <Box
      role="dialog"
      aria-label="Run detail"
      sx={{
        position: "fixed",
        top: 64,
        right: railW,
        bottom: 0,
        width: drawerW,
        bgcolor: T.rail,
        borderLeft: `1px solid ${T.hair}`,
        display: "flex",
        flexDirection: "row",
        zIndex: (t) => t.zIndex.appBar - 2,
        boxShadow: "0 0 24px rgba(0,0,0,0.06)",
      }}
    >
      <Box
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize run detail"
        onPointerDown={onPointerDown}
        sx={{
          width: T.drag_handle_w,
          cursor: "ew-resize",
          "&:hover": { bgcolor: T.accentBorder },
          flexShrink: 0,
        }}
      />
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            px: 1.5,
            py: 1,
            borderBottom: `1px solid ${T.hair}`,
            gap: 1,
          }}
        >
          <Typography sx={{ flex: 1, fontSize: T.fs_title, fontWeight: 600, color: T.text }}>
            Run {runId.slice(0, 8)}…
          </Typography>
          <Tooltip title="Replay run from start (Phase E)">
            <span>
              <IconButton
                size="small"
                onClick={handleReplay}
                disabled={replaying}
                sx={{ color: T.textMute }}
                aria-label="Replay run"
              >
                {replaying ? <CircularProgress size={16} /> : <ReplayIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Open run page">
            <IconButton
              size="small"
              component={RouterLink}
              to={`/runs/${runId}`}
              sx={{ color: T.textMute }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={onClose} aria-label="Close run detail">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {isLoading || !run ? (
          <Box display="flex" alignItems="center" justifyContent="center" flex={1}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflow: "auto", p: 1.5 }}>
            <Stack direction="row" spacing={1} mb={1.5} flexWrap="wrap">
              <Chip size="small" label={run.status} />
              <Chip size="small" label={`iter ${run.iterations}`} variant="outlined" />
              <Chip
                size="small"
                label={`$${Number(run.cost_usd ?? 0).toFixed(4)}`}
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${run.input_tokens}↑ / ${run.output_tokens}↓`}
                variant="outlined"
              />
            </Stack>

            {errObj && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                <Typography variant="body2" fontWeight={600}>
                  {errObj.code ?? "error"}
                </Typography>
                <Typography variant="body2">{errObj.message ?? JSON.stringify(errObj)}</Typography>
              </Alert>
            )}

            {iterationKeys.length === 0 ? (
              <Typography sx={{ color: T.textMute, fontSize: T.fs_meta }}>
                No tool calls recorded yet.
              </Typography>
            ) : (
              iterationKeys.map((iter) => (
                <Box key={iter} mb={1.5}>
                  <Typography
                    sx={{
                      color: T.textDim,
                      fontSize: T.fs_micro,
                      textTransform: "uppercase",
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Iteration {iter}
                  </Typography>
                  <Stack spacing={1}>
                    {(grouped.get(iter) ?? []).map((tc) => (
                      <ToolCallEntry key={tc.id} tc={tc} T={T} />
                    ))}
                  </Stack>
                  <Divider sx={{ mt: 1 }} />
                </Box>
              ))
            )}
          </Box>
        )}
      </Box>
      <Snackbar
        open={!!replaySnack}
        autoHideDuration={4000}
        onClose={() => setReplaySnack(null)}
        message={replaySnack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}

function ToolCallEntry({ tc, T }: { tc: ToolCallRow; T: ReturnType<typeof chatRailTokens> }) {
  const [open, setOpen] = useState(false);
  return (
    <Box
      sx={{
        border: `1px solid ${T.hair}`,
        borderRadius: `${T.r_sm}px`,
        p: 1,
        bgcolor: T.panel,
      }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => setOpen((v) => !v)}
        sx={{
          width: "100%",
          border: "none",
          bgcolor: "transparent",
          cursor: "pointer",
          textAlign: "left",
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 0,
          color: T.text,
        }}
      >
        <Chip size="small" label={tc.kind} variant="outlined" />
        <Typography
          sx={{
            flex: 1,
            fontFamily: T.mono,
            fontSize: T.fs_label,
            color: T.text,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {tc.skill_name}
        </Typography>
        <Typography sx={{ fontSize: T.fs_micro, color: T.textMute }}>
          {fmtDuration(tc.duration_ms)}
        </Typography>
        <Chip size="small" label={tc.status} variant="outlined" />
      </Box>
      {open && (
        <Stack spacing={1} mt={1}>
          <PreBlock label="Input" value={tc.input} T={T} />
          {tc.output != null && <PreBlock label="Output" value={tc.output} T={T} />}
          {tc.error && (
            <Alert severity="error" variant="outlined">
              {tc.error}
            </Alert>
          )}
        </Stack>
      )}
    </Box>
  );
}

function PreBlock({
  label,
  value,
  T,
}: {
  label: string;
  value: unknown;
  T: ReturnType<typeof chatRailTokens>;
}) {
  return (
    <Box>
      <Typography sx={{ fontSize: T.fs_micro, color: T.textMute, mb: 0.25 }}>{label}</Typography>
      <Box
        component="pre"
        sx={{
          fontFamily: T.mono,
          fontSize: T.fs_meta,
          color: T.text,
          bgcolor: T.panel2,
          border: `1px solid ${T.hair}`,
          borderRadius: `${T.r_xs}px`,
          p: 1,
          m: 0,
          maxHeight: 240,
          overflow: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
        }}
      >
        {typeof value === "string" ? value : JSON.stringify(value, null, 2)}
      </Box>
    </Box>
  );
}
